/*
  LUMISTELLE: Search Panel v1.1
  Companion to sections/lumistelle-search-panel.liquid.

  - SSR is the source of truth. Per term this file fetches two fragments of
    its own section through the Section Rendering API:
      /search/suggest  -> Collections and Help   (data-lum-sp-frag="suggest")
      /search          -> Pieces and See All      (data-lum-sp-frag="pieces")
    and only places them. No product markup is built here.
  - Order once she types: Help (only when a page title matches), Pieces,
    then Collections. Pieces lead because they are what a search is for and
    the keyboard leaves room for little else. Before typing, Where to Start.
  - The Enter button in the field shows with the clear button, so a search
    can be sent with the keyboard closed.
  - Opens on the same tap as the header search icon and focuses the field
    synchronously, so the iPhone keyboard opens.
  - Takes over .lumi-hdr__search-btn, .header-search-toggle and any
    [data-lum-search-open] element in the capture phase, so the old modal
    never runs while this panel exists (the section renders nothing when off).
  - History: opening pushes one entry; Back closes. Returning to that entry
    (Back from a product, even without bfcache) reopens the panel on the
    same term.
*/
(function () {
  'use strict';

  if (window.LumistelleSearchPanel) return;

  var OPENERS = '.lumi-hdr__search-btn, .header-search-toggle, [data-lum-search-open]';
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([type="hidden"])';
  var active = null;

  function loadImg(img) {
    var src = img.getAttribute('data-src');
    if (!src) return;
    img.src = src;
    img.removeAttribute('data-src');
  }

  function Panel(root) {
    this.root = root;
    this.sid = root.getAttribute('data-section-id');
    this.minChars = parseInt(root.getAttribute('data-min-chars'), 10) || 2;
    this.useSuggest = root.getAttribute('data-suggest') !== 'false';
    this.inStock = root.getAttribute('data-in-stock') === 'true';
    this.suggestUrl = root.getAttribute('data-suggest-url') || '/search/suggest';
    this.searchUrl = root.getAttribute('data-search-url') || '/search';
    this.noResults = root.getAttribute('data-no-results') || '';
    this.input = root.querySelector('[data-lum-sp-input]');
    this.form = this.input.form;
    this.clearBtn = root.querySelector('[data-lum-sp-clear]');
    this.goBtn = root.querySelector('[data-lum-sp-go]');
    this.closeBtn = root.querySelector('[data-lum-sp-close]');
    this.startEl = root.querySelector('[data-lum-sp-start]');
    this.resultsEl = root.querySelector('[data-lum-sp-results]');
    this.statusEl = root.querySelector('[data-lum-sp-status]');
    this.design = !!(window.Shopify && window.Shopify.designMode);
    this.isOpen = false;
    this.opener = null;
    this.timer = null;
    this.ctrl = null;
    this.shown = '';
    this.cache = {};
    this.observers = [];

    /* Out of the header group: no ancestor transform can trap position: fixed */
    if (root.parentNode !== document.body) document.body.appendChild(root);

    this.onInput = this.onInput.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onPop = this.onPop.bind(this);
    this.requestClose = this.requestClose.bind(this);

    this.input.addEventListener('input', this.onInput);
    this.root.addEventListener('keydown', this.onKeydown);
    this.closeBtn.addEventListener('click', this.requestClose);
    this.clearBtn.addEventListener('click', function () {
      this.input.value = '';
      this.onInput();
      this.input.focus();
    }.bind(this));
    this.form.addEventListener('submit', function (e) {
      if (!this.input.value.trim()) e.preventDefault();
    }.bind(this));
    window.addEventListener('popstate', this.onPop);

    /* Landed back on a panel history entry without bfcache: reopen on the same term */
    var st = history.state;
    if (!this.design && st && st.lumSP) this.open({ focus: false, push: false, q: st.q || '' });
  }

  Panel.prototype.loadShellImages = function () {
    if (this.shellLoaded) return;
    this.shellLoaded = true;
    Array.prototype.forEach.call(this.root.querySelectorAll('.lum-sp__top img[data-src], [data-lum-sp-start] img[data-src]'), loadImg);
  };

  Panel.prototype.open = function (opts) {
    opts = opts || {};
    if (this.isOpen) {
      if (opts.focus !== false) this.input.focus({ preventScroll: true });
      return;
    }
    this.isOpen = true;
    /* Performance tool (window.LumistellePerf exists only for tagged testers) */
    var perfSpan = window.LumistellePerf && window.LumistellePerf.span('search panel: open');
    this.opener = opts.opener || document.activeElement;
    this.loadShellImages();
    this.root.hidden = false;
    document.documentElement.classList.add('lum-sp-lock');

    var q = opts.q;
    if (q == null && location.pathname === this.searchUrl && !this.input.value) {
      q = new URLSearchParams(location.search).get('q') || '';
    }
    if (q != null && q !== '') this.input.value = q;

    /* Same tap as the icon: this is what opens the keyboard on iOS */
    if (opts.focus !== false) {
      this.input.focus({ preventScroll: true });
      try {
        var end = this.input.value.length;
        this.input.setSelectionRange(end, end);
      } catch (e) {}
    }

    this.onInput(true);
    var root = this.root;
    requestAnimationFrame(function () {
      root.classList.add('is-open');
      if (perfSpan) perfSpan.end();
    });

    if (!this.design && opts.push !== false) {
      history.pushState({ lumSP: true, q: this.input.value.trim() }, '');
    }
  };

  Panel.prototype.requestClose = function () {
    if (!this.design && history.state && history.state.lumSP) {
      history.back(); /* popstate closes it, so Back and the close icon behave the same */
    } else {
      this.closeNow();
    }
  };

  Panel.prototype.closeNow = function () {
    if (!this.isOpen) return;
    this.isOpen = false;
    clearTimeout(this.timer);
    this.abort();
    this.root.classList.remove('is-open');
    document.documentElement.classList.remove('lum-sp-lock');
    var self = this;
    setTimeout(function () { if (!self.isOpen) self.root.hidden = true; }, 180);
    if (this.opener && this.opener.focus && document.contains(this.opener)) {
      this.opener.focus({ preventScroll: true });
    }
  };

  Panel.prototype.onPop = function () {
    var st = history.state;
    if (st && st.lumSP) {
      if (!this.isOpen) this.open({ focus: false, push: false, q: st.q || '' });
    } else if (this.isOpen) {
      this.closeNow();
    }
  };

  Panel.prototype.onKeydown = function (e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      this.requestClose();
      return;
    }
    if (e.key !== 'Tab') return;
    var items = Array.prototype.filter.call(this.root.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetParent !== null && !el.hidden;
    });
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  Panel.prototype.onInput = function (immediate) {
    var q = this.input.value.trim();
    this.clearBtn.hidden = !this.input.value.length;
    if (this.goBtn) this.goBtn.hidden = !q.length;
    clearTimeout(this.timer);

    if (q.length < this.minChars) {
      this.abort();
      this.resultsEl.innerHTML = '';
      this.shown = '';
      this.setLoading(false);
      this.startEl.hidden = false;
      this.status('');
      return;
    }

    this.startEl.hidden = true;
    /* Never leave another word's results on screen while the new ones load */
    if (this.shown && q.toLowerCase().indexOf(this.shown.toLowerCase()) !== 0) {
      this.resultsEl.innerHTML = '';
      this.shown = '';
    }
    var self = this;
    this.timer = setTimeout(function () { self.search(q); }, immediate === true ? 0 : 250);
  };

  Panel.prototype.abort = function () {
    if (this.ctrl) {
      this.ctrl.abort();
      this.ctrl = null;
    }
  };

  Panel.prototype.setLoading = function (on) {
    this.resultsEl.classList.toggle('is-loading', on);
    this.resultsEl.setAttribute('aria-busy', on ? 'true' : 'false');
  };

  Panel.prototype.status = function (text) {
    if (this.statusEl) this.statusEl.textContent = text;
  };

  Panel.prototype.suggestUrlFor = function (q) {
    var u = new URL(this.suggestUrl, location.origin);
    u.searchParams.set('q', q);
    u.searchParams.set('section_id', this.sid);
    u.searchParams.set('resources[type]', 'collection,page');
    u.searchParams.set('resources[limit]', '10');
    u.searchParams.set('resources[limit_scope]', 'each');
    return u.toString();
  };

  Panel.prototype.piecesUrlFor = function (q) {
    var u = new URL(this.searchUrl, location.origin);
    u.searchParams.set('q', q);
    u.searchParams.set('options[prefix]', 'last');
    u.searchParams.set('type', 'product');
    if (this.inStock) u.searchParams.set('filter.v.availability', '1');
    u.searchParams.set('section_id', this.sid);
    return u.toString();
  };

  /* Resolves { html, count, parts } or null on failure; rethrows aborts.
     parts holds each [data-lum-sp-part] group so render() can order them. */
  Panel.prototype.fetchFragment = function (url, name, signal) {
    return fetch(url, { signal: signal, credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (text) {
        var tpl = document.createElement('template');
        tpl.innerHTML = text;
        var frag = tpl.content.querySelector('[data-lum-sp-frag="' + name + '"]');
        if (!frag) return { html: '', count: 0, parts: {} };
        var parts = {};
        Array.prototype.forEach.call(frag.querySelectorAll('[data-lum-sp-part]'), function (el) {
          parts[el.getAttribute('data-lum-sp-part')] = el.outerHTML;
        });
        return {
          html: frag.innerHTML.trim(),
          count: parseInt(frag.getAttribute('data-count'), 10) || 0,
          parts: parts
        };
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') throw err;
        return null;
      });
  };

  Panel.prototype.search = function (q) {
    var key = q.toLowerCase();
    if (this.cache[key]) {
      this.render(q, this.cache[key]);
      return;
    }
    this.abort();
    var ctrl = new AbortController();
    this.ctrl = ctrl;
    this.setLoading(true);

    var self = this;
    var empty = Promise.resolve({ html: '', count: 0, parts: {} });
    Promise.all([
      this.useSuggest ? this.fetchFragment(this.suggestUrlFor(q), 'suggest', ctrl.signal) : empty,
      this.fetchFragment(this.piecesUrlFor(q), 'pieces', ctrl.signal)
    ]).then(function (parts) {
      if (ctrl.signal.aborted) return;
      var data = { suggest: parts[0], pieces: parts[1] };
      if (data.suggest && data.pieces) self.cache[key] = data;
      if (self.input.value.trim().toLowerCase() !== key) return;
      self.render(q, data);
    }).catch(function () {});
  };

  Panel.prototype.render = function (q, data) {
    this.setLoading(false);
    this.observers.forEach(function (io) { io.disconnect(); });
    this.observers = [];

    var s = data.suggest ? data.suggest.html : '';
    var p = data.pieces ? data.pieces.html : '';
    var parts = (data.suggest && data.suggest.parts) || {};

    if (!s && !p) {
      this.resultsEl.innerHTML = '';
      /* Only say "nothing matches" when both searches actually answered */
      if (data.suggest && data.pieces) {
        var msg = document.createElement('p');
        msg.className = 'lum-sp__msg';
        msg.textContent = this.noResults.split('[term]').join(q);
        this.resultsEl.appendChild(msg);
        var start = this.startEl.querySelector('.lum-sp__group');
        if (start) this.resultsEl.appendChild(start.cloneNode(true));
        this.status(msg.textContent);
      }
    } else {
      /* Help, Pieces, Collections, then See All last */
      var help = parts.help || '';
      var collections = parts.collections || '';
      if (!help && !collections) collections = s;
      var pp = (data.pieces && data.pieces.parts) || {};
      var pieces = pp.pieces || p;
      var seeAll = pp.pieces ? (pp.all || '') : '';
      this.resultsEl.innerHTML = help + pieces + collections + seeAll;
      this.lazyLoad();
      var count = data.pieces ? data.pieces.count : 0;
      this.status(count ? count + (count === 1 ? ' piece' : ' pieces') : '');
    }

    this.shown = q;
    if (!this.design && history.state && history.state.lumSP) {
      history.replaceState({ lumSP: true, q: q }, '');
    }
  };

  /* Pieces past the first 8 arrive as data-src; load them a little ahead of the swipe */
  Panel.prototype.lazyLoad = function () {
    var self = this;
    Array.prototype.forEach.call(this.resultsEl.querySelectorAll('.lum-sp__carousel'), function (track) {
      var imgs = track.querySelectorAll('img[data-src]');
      if (!imgs.length) return;
      if (!('IntersectionObserver' in window)) {
        Array.prototype.forEach.call(imgs, loadImg);
        return;
      }
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          loadImg(entry.target);
          io.unobserve(entry.target);
        });
      }, { root: track, rootMargin: '0px 320px 0px 320px' });
      Array.prototype.forEach.call(imgs, function (img) { io.observe(img); });
      self.observers.push(io);
    });
  };

  Panel.prototype.destroy = function () {
    this.closeNow();
    this.abort();
    this.observers.forEach(function (io) { io.disconnect(); });
    window.removeEventListener('popstate', this.onPop);
    document.documentElement.classList.remove('lum-sp-lock');
    if (this.root.parentNode) this.root.parentNode.removeChild(this.root);
  };

  /* Capture phase: runs before the header's own listeners and stops them */
  document.addEventListener('click', function (e) {
    if (!active || !e.target.closest) return;
    var trigger = e.target.closest(OPENERS);
    if (!trigger || active.root.contains(trigger)) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    active.open({ opener: trigger });
  }, true);

  function boot() {
    if (active) return;
    var el = document.querySelector('[data-lum-sp]');
    if (el) active = new Panel(el);
  }

  /* Theme editor */
  document.addEventListener('shopify:section:load', function (e) {
    var el = document.querySelector('#shopify-section-' + e.detail.sectionId + ' [data-lum-sp]');
    if (!el) return;
    if (active) active.destroy();
    active = new Panel(el);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    if (active && active.sid === e.detail.sectionId) {
      active.destroy();
      active = null;
    }
  });
  document.addEventListener('shopify:section:select', function (e) {
    if (active && active.sid === e.detail.sectionId) active.open({ focus: false, push: false });
  });
  document.addEventListener('shopify:section:deselect', function (e) {
    if (active && active.sid === e.detail.sectionId) active.closeNow();
  });

  window.LumistelleSearchPanel = {
    open: function (q) { if (active) active.open({ q: q }); },
    close: function () { if (active) active.requestClose(); }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();